import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function TrialBooking() {
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", test_type: "IELTS_ACADEMIC",
    target_score: "", current_level: "INTERMEDIATE", preferred_date: "", preferred_time: "", notes: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/trial/book", form);
      setDone(true);
      toast.success("Booking received — we'll reach out within 24 hours.");
    } catch (e) { toast.error("Booking failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] text-slate-900 relative overflow-hidden">
      <div className="aurora" />
      <nav className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center surface-navy"><span className="font-display text-white text-sm font-bold">L</span></div>
          <span className="font-display text-lg text-[#003049]">LingoQ</span>
        </Link>
        <Link to="/login" className="text-sm text-slate-500 hover:text-[#003049]">Login</Link>
      </nav>
      <div className="relative z-10 max-w-2xl mx-auto px-6 pb-20 pt-10">
        {done ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center shadow-[0_30px_60px_-30px_rgba(11,45,92,0.15)]">
            <CheckCircle2 className="w-12 h-12 text-[#16A34A] mx-auto mb-4" />
            <h1 className="font-display text-3xl text-[#003049]">You're booked!</h1>
            <p className="text-slate-600 mt-3">Our team will confirm your free trial via email within 24h.</p>
            <Link to="/" data-testid="trial-back-home" className="mt-8 btn-primary inline-flex items-center gap-2">
              Back home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="kicker mb-3">Free Trial Class</div>
            <h1 className="font-display text-4xl mb-3 text-[#003049]">Book your free trial.</h1>
            <p className="text-slate-600 mb-8">Meet a mentor, see the platform live, no payment required.</p>
            <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-8 space-y-4 shadow-[0_30px_60px_-30px_rgba(11,45,92,0.12)]">
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="trial-name" required placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input-base" />
                <input data-testid="trial-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base" />
              </div>
              <input data-testid="trial-phone" placeholder="Phone (with country code)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-base" />
              <div className="grid sm:grid-cols-2 gap-3">
                <select data-testid="trial-test" value={form.test_type} onChange={(e) => setForm({ ...form, test_type: e.target.value })} className="input-base">
                  <option value="IELTS_ACADEMIC">IELTS Academic</option>
                  <option value="IELTS_GENERAL">IELTS General</option>
                  <option value="PTE">PTE Academic</option>
                  <option value="OXFORD_ELLT">Oxford ELLT</option>
                  <option value="DUOLINGO">Duolingo</option>
                </select>
                <input data-testid="trial-target" placeholder="Target score (e.g. 7.5)" value={form.target_score} onChange={(e) => setForm({ ...form, target_score: e.target.value })} className="input-base" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="trial-date" type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="input-base" />
                <input data-testid="trial-time" type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="input-base" />
              </div>
              <textarea data-testid="trial-notes" rows={3} placeholder="Anything you'd like us to know?" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-base" />
              <button type="submit" disabled={loading} data-testid="trial-submit" className="w-full btn-primary disabled:opacity-50">
                {loading ? "Booking..." : "Book Free Trial"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
